import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DollarSign,
  TrendingUp,
  Users,
  UserMinus,
  Percent,
  Copy,
  Sparkles,
  Share2,
  BarChart3,
  MousePointerClick,
  ArrowRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ActionQueue, { type ActionItem } from "./ActionQueue";
import ReferralShareKit from "./ReferralShareKit";
import type { PatientRecord } from "./PatientPipeline";

interface SalesOverviewProps {
  totalRevenue: number;
  totalCommissions: number;
  activePatients: number;
  churnedPatients: number;
  conversionRate: number;
  barData: { month: string; revenue: number }[];
  actionItems: ActionItem[];
  referralLink: string;
  formatAmount: (cents: number) => string;
  referralClicks: number;
}

const SalesOverview = ({
  totalRevenue,
  totalCommissions,
  activePatients,
  churnedPatients,
  conversionRate,
  barData,
  actionItems,
  referralLink,
  formatAmount,
  referralClicks,
}: SalesOverviewProps) => {
  const kpis = [
    { label: "Total Revenue", value: formatAmount(totalRevenue), icon: DollarSign, color: "text-primary" },
    { label: "Commissions", value: formatAmount(totalCommissions), icon: TrendingUp, color: "text-champagne" },
    { label: "Active Patients", value: activePatients, icon: Users, color: "text-primary" },
    { label: "Churned", value: churnedPatients, icon: UserMinus, color: "text-destructive" },
    { label: "Conversion Rate", value: `${conversionRate}%`, icon: Percent, color: "text-foreground" },
  ];

  const hasData = totalRevenue > 0 || activePatients > 0;

  return (
    <div className="space-y-6">
      {/* Onboarding empty state */}
      {!hasData && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-primary" />
              </div>
              <div className="space-y-3">
                <div>
                  <h3 className="font-display text-lg text-foreground">Welcome to Your Sales Dashboard</h3>
                  <p className="text-sm text-muted-foreground">Get started in 3 simple steps:</p>
                </div>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</div>
                    <span><Copy size={12} className="inline mr-1" />Copy your referral link below</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">2</div>
                    <span><Share2 size={12} className="inline mr-1" />Share it with potential patients</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">3</div>
                    <span><BarChart3 size={12} className="inline mr-1" />Track your conversions and commissions here</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className={k.color} />
              </div>
              <p className="font-display text-2xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Referral Funnel Widget */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <MousePointerClick size={16} /> Referral Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Clicks */}
            <div className="flex flex-col items-center p-4 rounded-lg bg-secondary/30 border border-border">
              <MousePointerClick size={20} className="text-muted-foreground mb-2" />
              <p className="font-display text-3xl text-foreground">{referralClicks}</p>
              <p className="text-xs text-muted-foreground mt-1">Link Clicks</p>
            </div>

            <div className="hidden sm:flex items-center justify-center">
              <ArrowRight size={24} className="text-muted-foreground/40" />
            </div>

            {/* Conversions */}
            <div className="flex flex-col items-center p-4 rounded-lg bg-secondary/30 border border-border">
              <Users size={20} className="text-primary mb-2" />
              <p className="font-display text-3xl text-foreground">{activePatients}</p>
              <p className="text-xs text-muted-foreground mt-1">Conversions</p>
            </div>
          </div>

          {/* Conversion Rate Bar */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Click → Conversion Rate</span>
              <span className="font-display text-foreground font-medium">
                {referralClicks > 0 ? ((activePatients / referralClicks) * 100).toFixed(1) : "0.0"}%
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{
                  width: `${referralClicks > 0 ? Math.min((activePatients / referralClicks) * 100, 100) : 0}%`,
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Queue */}
      <ActionQueue items={actionItems} />

      {/* Referral Share Kit */}
      <ReferralShareKit referralLink={referralLink} />

      {/* Revenue Chart */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <TrendingUp size={16} /> Monthly Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          {barData.every((d) => d.revenue === 0) ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No revenue data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                  formatter={(value: number) => [`$${value.toFixed(2)}`, "Revenue"]}
                />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesOverview;
